import React, { useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Button,
  Collapse,
  Box,
  makeStyles,
} from "@material-ui/core";
import { ExpandMore, ExpandLess } from "@material-ui/icons";

import InactiveStudentsPanel from "./InactiveStudentsPanel";

// TZ-786 Step 5: per-team activation breakdown. Each row shows the
// three counters plus a "View inactive" toggle. Expansion is
// single-team-at-a-time — opening a second team collapses the first.
// Rationale: the named list endpoint is a separate API call per team,
// and admins almost always want to scan one team's no-shows at a time
// (the spec frames this as "who hasn't activated yet on team X"),
// not compare two lists side by side.

const useStyles = makeStyles(() => ({
  emptyPaper:    { padding: 24 },
  // Collapse-row cell: zero vertical padding so the inactive panel
  // sits flush under the data row. borderBottom is suppressed when
  // collapsed so the table doesn't render a hairline gap between the
  // data row and the next team's data row.
  collapseCell:        { paddingTop: 0, paddingBottom: 0 },
  collapseCellClosed:  { borderBottom: "none" },
}));

const ActivationTeamsTable = ({ perTeam }) => {
  const classes = useStyles();
  const [expandedTeamId, setExpandedTeamId] = useState(null);

  if (!perTeam || perTeam.length === 0) {
    return (
      <Paper className={classes.emptyPaper}>
        <Typography variant="body2" color="textSecondary">
          No teams visible for this school yet — upload a roster first, or
          if you're a coach, ask the org admin to add you to a team.
        </Typography>
      </Paper>
    );
  }

  const toggle = (teamId) =>
    setExpandedTeamId((prev) => (prev === teamId ? null : teamId));

  return (
    <Paper>
      <Box p={2} pb={0}>
        <Typography variant="h6">Per-team breakdown</Typography>
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Team</TableCell>
            <TableCell align="right">Total</TableCell>
            <TableCell align="right">Activated</TableCell>
            <TableCell align="right">Unactivated</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {perTeam.map((row) => {
            const isOpen = expandedTeamId === row.team_id;
            // No "View inactive" affordance when there's nothing to
            // expand to — saves an empty-state round trip and signals
            // to the admin at a glance which teams are fully on.
            const canExpand = row.unactivated > 0;
            return (
              <React.Fragment key={row.team_id}>
                <TableRow>
                  <TableCell>{row.team_name}</TableCell>
                  <TableCell align="right">{row.total}</TableCell>
                  <TableCell align="right">{row.activated}</TableCell>
                  <TableCell align="right">{row.unactivated}</TableCell>
                  <TableCell align="right">
                    {canExpand && (
                      <Button
                        size="small"
                        onClick={() => toggle(row.team_id)}
                        startIcon={isOpen ? <ExpandLess /> : <ExpandMore />}
                      >
                        {isOpen ? "Hide inactive" : "View inactive"}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className={clsx(classes.collapseCell, !isOpen && classes.collapseCellClosed)}
                  >
                    <Collapse in={isOpen} unmountOnExit>
                      {isOpen && (
                        <InactiveStudentsPanel
                          teamId={row.team_id}
                          teamName={row.team_name}
                        />
                      )}
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
};

ActivationTeamsTable.propTypes = {
  perTeam: PropTypes.arrayOf(
    PropTypes.shape({
      team_id:     PropTypes.number.isRequired,
      team_name:   PropTypes.string.isRequired,
      total:       PropTypes.number.isRequired,
      activated:   PropTypes.number.isRequired,
      unactivated: PropTypes.number.isRequired,
    })
  ).isRequired,
};

export default ActivationTeamsTable;
